import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { clearSession, getSession } from "@/lib/auth";
import { LogoMark } from "@/lib/Logo";
import { BRAND } from "@/lib/constants";
import { useSeo } from "@/lib/seo";

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const FRIENDLY_STATUS = { open: "Under process", closed: "Confirmed" };

export default function Account() {
  const session = getSession();
  const [user, setUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const navigate = useNavigate();
  useSeo("My Enquiries | Delhi Airport Stay & Transit Services", "Track your room enquiries and their status.");

  useEffect(() => {
    if (!session) return;
    api.get("/auth/me")
      .then((r) => {
        if (r.data.role !== "guest") {
          navigate("/admin");
          return;
        }
        setUser(r.data);
        api.get("/inquiries/mine").then((res) => setInquiries(res.data)).catch(() => {});
      })
      .catch(() => setUser(false));
  }, []);

  if (!session) return <Navigate to="/login" replace />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user === null) return <div className="admin-loading" data-testid="account-loading">Loading your account…</div>;

  const logout = async () => {
    clearSession();
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/login");
  };

  return (
    <div className="admin-shell" data-testid="guest-account">
      <header className="admin-topbar">
        <div className="brand">
          <LogoMark size={30} />
          <span>
            <strong>{BRAND}</strong>
            <small>Namaste, {user.name}</small>
          </span>
        </div>
        <div className="admin-actions">
          <Link to="/#enquiry" className="button button-gold" data-testid="new-enquiry-link">
            New enquiry <ArrowRight size={15} />
          </Link>
          <Link to="/" className="button button-ghost" data-testid="account-home-link">Home</Link>
          <button className="button button-dark" onClick={logout} data-testid="guest-logout-button">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <h2 className="account-title">My enquiries</h2>
      {inquiries.length === 0 ? (
        <div className="empty-state" data-testid="empty-my-enquiries">
          No enquiries yet. Send one from the website and track it here.
        </div>
      ) : (
        <div className="inquiry-list">
          {inquiries.map((inq) => (
            <article className={`inquiry-card ${inq.status}`} key={inq.id} data-testid={`my-enquiry-${inq.id}`}>
              <div className="inquiry-head">
                <div>
                  <strong>{inq.room || "Room enquiry"}</strong>
                  <span className="inquiry-meta">
                    {inq.check_in || "Date TBD"} · {inq.guests} guest(s) · {inq.area || "Area TBD"} · sent {fmtDate(inq.created_at)}
                  </span>
                </div>
                <span className={`status-badge ${inq.status}`} data-testid={`my-enquiry-status-${inq.id}`}>
                  {FRIENDLY_STATUS[inq.status] || inq.status}
                </span>
              </div>
              <p className="account-note">
                {inq.status === "closed"
                  ? "Your booking is confirmed. Details were shared with you on chat."
                  : "Sudheer Kumar will contact you on WhatsApp with availability and room photos."}
              </p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
